import { useEffect, useState } from 'react'
import { ChevronRight, Zap } from 'lucide-react'
import { billingApi } from '@/lib/billingApi'
import { useAppStore } from '@/store/app'
import type { Guild } from '@/types/api'
import styles from './BoostProgress.module.css'

// пороги уровней одни на всё приложение: грузим один раз, при ошибке — заново в следующий раз
let needsOnce: Promise<number[]> | null = null
function boostNeeds(): Promise<number[]> {
  needsOnce ??= billingApi.showcase().then((s) => s.boosts).catch((err: unknown) => {
    needsOnce = null
    throw err
  })
  return needsOnce
}

/**
 * Полоска бустов над каналами (premium_progress_bar_enabled, как у Discord): сколько бустов до следующего
 * уровня, на последнем — сколько всего. Ведёт во вкладку «Буст».
 */
export function BoostProgress({ guild }: { guild: Guild }) {
  const openGuildSettings = useAppStore((s) => s.openGuildSettings)
  const [needs, setNeeds] = useState<number[] | null>(null)

  useEffect(() => {
    let alive = true
    boostNeeds().then((n) => { if (alive) setNeeds(n) }).catch(() => undefined)
    return () => { alive = false }
  }, [])

  if (!needs?.length) return null
  const tier = guild.premium_tier
  const count = guild.premium_subscription_count
  const next = tier < needs.length ? tier + 1 : null
  const from = tier > 0 ? (needs[tier - 1] ?? 0) : 0
  const to = next ? (needs[next - 1] ?? from) : from
  const pct = next && to > from ? Math.min(100, Math.max(0, ((count - from) / (to - from)) * 100)) : 100
  const label = next ? `До уровня ${next}` : `Уровень ${tier}`
  const value = next ? `${count} из ${to}` : `Бусты: ${count}`

  return (
    <button type="button" className={styles.bar} data-tooltip="Буст сервера" aria-label={`${label}: ${value}`} onClick={() => openGuildSettings(guild.id, 'boost')}>
      <span className={styles.fill} style={{ width: `${pct}%` }} aria-hidden="true" />
      <span className={styles.label}><Zap size={14} aria-hidden="true" /> {label}</span>
      <span className={styles.value}>{value} <ChevronRight size={14} aria-hidden="true" /></span>
    </button>
  )
}
